import React, { useState, useEffect, useRef } from 'react';
import Head from 'next/head';
import Layout from '../components/Layout';
import { fetcher } from '../helper/fetcher';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, EffectFade, Thumbs } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import { FiMaximize2 } from '@react-icons/all-files/fi/FiMaximize2';
import { FiMapPin } from '@react-icons/all-files/fi/FiMapPin';
import { FiCheckCircle } from '@react-icons/all-files/fi/FiCheckCircle';
import { FiPhone } from '@react-icons/all-files/fi/FiPhone';
import { FiMail } from '@react-icons/all-files/fi/FiMail';
import { FiHome } from '@react-icons/all-files/fi/FiHome';
import { FiMap } from '@react-icons/all-files/fi/FiMap';
import { FiDroplet } from '@react-icons/all-files/fi/FiDroplet';
import { FiSun } from '@react-icons/all-files/fi/FiSun';
import { FiDownload } from '@react-icons/all-files/fi/FiDownload';
import { FiFileText } from '@react-icons/all-files/fi/FiFileText';
import { FaWhatsapp } from '@react-icons/all-files/fa/FaWhatsapp';
import dynamic from 'next/dynamic';

const Fancybox = dynamic(() => import('../components/Fancybox'), { ssr: false });

// Dinamik olarak galeri resimlerini oluşturuyoruz (resized klasörü kullanılarak)
const galleryImages = [
  "https://cdn.villavillam.com.tr/lujo-emo-villas/galeri/resized/lujo_emo_fethiye.jpg",
  ...Array.from({ length: 60 }, (_, i) => `https://cdn.villavillam.com.tr/lujo-emo-villas/galeri/resized/lujo_emo_fethiye%20(${i + 2}).jpg`)
];

// Hero arka planı olarak Drone/Dış Cephe fotoğrafı (1. resim)
const heroImage = galleryImages[0];
// İletişim arka planı olarak farklı bir dış cephe fotoğrafı (örneğin 5. resim)
const ctaImage = galleryImages[4] || galleryImages[1];
// Video posteri olarak iç mekan veya havuz (örneğin 10. resim)
const videoPoster = galleryImages[9] || galleryImages[2];

const features = [
  "4+1 Oda Seçeneği",
  "500 m² Arsa Payı",
  "372 m² Kullanım Alanı",
  "Yerden Isıtma Sistemi",
  "Multi Klima Sistem",
  "Boyler Güneş Enerjisi",
  "Su Yumuşatıcı Sistem",
  "Doğal Mermer/Traverten",
  "Hamam, Sauna, Kapalı Havuz",
  "Müstakil Dış Havuz (50 m²)"
];

const floorPlans = [
  { id: 'vaziyet', name: 'Vaziyet Planı', url: 'https://cdn.villavillam.com.tr/lujo-emo-villas/kat-planlari/lujo-emo-vaziyet-plan.pdf' },
  { id: 'bodrum', name: 'Bodrum Kat', url: 'https://cdn.villavillam.com.tr/lujo-emo-villas/kat-planlari/lujo-emo-bodrum-kat-plan.pdf' },
  { id: 'zemin', name: 'Zemin Kat', url: 'https://cdn.villavillam.com.tr/lujo-emo-villas/kat-planlari/lujo-emo-zemin-kat-plan.pdf' },
  { id: 'birinci', name: '1. Kat', url: 'https://cdn.villavillam.com.tr/lujo-emo-villas/kat-planlari/lujo-emo-1-kat-plan.pdf' },
  { id: 'cati', name: 'Çatı Katı', url: 'https://cdn.villavillam.com.tr/lujo-emo-villas/kat-planlari/lujo-emo-cati-kat-plan.pdf' }
];

export default function SatilikVilla({ data }) {
  const [hoveredLoc, setHoveredLoc] = useState(null);
  const mapIframeRef = useRef(null);
  
  const mainSwiperRef = useRef(null);
  const thumbsSwiperRef = useRef(null);

  const handleSlideChange = (swiper) => {
    const thumbs = thumbsSwiperRef.current;
    if (!thumbs || thumbs.destroyed) return;
    const slides = thumbs.slides;
    if (!slides) return;
    for (let i = 0; i < slides.length; i++) {
      if (i === swiper.activeIndex) {
        slides[i].classList.add('opacity-100', 'border-brand-color-primary');
        slides[i].classList.remove('opacity-50', 'border-transparent');
      } else {
        slides[i].classList.remove('opacity-100', 'border-brand-color-primary');
        slides[i].classList.add('opacity-50', 'border-transparent');
      }
    }
    // Also scroll thumbs swiper to active slide if needed
    thumbs.slideTo(swiper.activeIndex);
  };

  useEffect(() => {
    if (mapIframeRef.current && mapIframeRef.current.contentWindow) {
      mapIframeRef.current.contentWindow.postMessage({ type: 'HOVER_LOC', id: hoveredLoc }, '*');
    }
  }, [hoveredLoc]);

  return (
    <Layout ssr={data}>
      <Head>
        <title>Lujo Emo Villas Fethiye - Lansmana Özel Satılık Villalar</title>
        <meta name="description" content="Lujo Emo Villas Fethiye projemizde yer alan sınırlı sayıdaki premium satılık villalarımızı inceleyin." />
      </Head>

      {/* NOTE: relative z-0 ensures the global Header's dropdown menu (z-50) stays above this page's content */}
      <main className="relative z-0 w-full bg-slate-50 min-h-screen font-sans">
        
        {/* HERO SECTION */}
        <section className="relative w-full h-[65vh] md:h-[85vh] lg:h-screen flex items-center justify-center overflow-hidden">
          {/* Corner Ribbon */}
          <div className="absolute top-0 right-0 z-40 w-36 h-36 md:w-48 md:h-48 overflow-hidden pointer-events-none">
            <div className="absolute top-8 -right-8 w-48 md:top-12 md:-right-12 md:w-64 text-white text-center py-1 md:py-2 font-bold text-[9px] md:text-sm uppercase tracking-[0.1em] md:tracking-[0.2em] shadow-xl transform rotate-45 border-y border-white/20" style={{ backgroundColor: '#f26c22' }}>
              HEMEN TESLİM !
            </div>
          </div>
          
          {/* Background Image */}
          <div className="absolute inset-0 z-0">
            <Swiper
              modules={[Autoplay, EffectFade]}
              effect="fade"
              autoplay={{ delay: 5000, disableOnInteraction: false }}
              allowTouchMove={false}
              className="w-full h-full"
            >
              {[galleryImages[0], galleryImages[4], galleryImages[2]].map((src, index) => (
                <SwiperSlide key={index}>
                  <img 
                    src={src} 
                    alt={`Lujo Emo Villas Fethiye - ${index + 1}`} 
                    className="w-full h-full object-cover scale-105 transform origin-center" 
                    style={{ animation: 'scaleUp 20s ease-out infinite alternate' }}
                  />
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/80"></div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          <div className="relative z-10 text-center px-4 max-w-4xl mx-auto mt-4 md:mt-20">
            <span className="inline-block py-1 px-3 border border-white/40 backdrop-blur-md rounded-full text-white/90 text-xs md:text-sm tracking-[0.2em] uppercase mb-4 md:mb-6">
              Sınırlı Üretim • Sadece 5 Adet
            </span>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-light text-white tracking-tight mb-4 md:mb-6 drop-shadow-lg">
              Lujo Emo Villas <span className="font-bold">Fethiye</span>
            </h1>
            <p className="text-base md:text-xl text-white/90 font-light mb-8 md:mb-10 max-w-2xl mx-auto leading-relaxed drop-shadow">
              Modern mimarinin doğa ile kusursuz uyumu. Yaşam standartlarınızı yeniden tanımlayacak elit bir proje.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
              <a href="#iletisim" className="inline-block bg-white text-black px-6 py-3 md:px-8 md:py-4 rounded-full font-medium tracking-wide hover:bg-brand-color-primary hover:text-white transition-colors duration-300 shadow-xl text-sm md:text-base">
                Hemen Bilgi Alın
              </a>
              <a href="https://cdn.villavillam.com.tr/lujo-emo-villas/katalog.pdf" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white px-6 py-3 md:px-8 md:py-4 rounded-full font-medium tracking-wide hover:bg-white hover:text-black transition-colors duration-300 shadow-xl text-sm md:text-base">
                <FiDownload /> Kataloğu İndir
              </a>
            </div>
          </div>

          {/* Scroll Down Indicator */}
          <div className="absolute bottom-4 md:bottom-10 left-1/2 transform -translate-x-1/2 z-10 hidden sm:flex flex-col items-center animate-bounce">
            <span className="text-white/70 text-xs tracking-widest uppercase mb-2">Keşfet</span>
            <div className="w-[1px] h-12 bg-gradient-to-b from-white/70 to-transparent"></div>
          </div>
        </section>

        {/* QUICK STATS BAR */}
        <section className="relative z-20 -mt-10 md:-mt-16 mx-4 md:mx-auto max-w-5xl bg-white/95 backdrop-blur-2xl rounded-2xl md:rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.1)] p-5 md:p-8 border border-white/60">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 md:gap-8 text-center divide-x divide-gray-100/50">
            <div className="p-1 md:p-2 group">
              <span className="text-brand-color-primary text-3xl md:text-4xl mb-2 md:mb-4 flex justify-center transform group-hover:scale-110 transition-transform duration-300"><FiHome /></span>
              <span className="block text-gray-900 font-bold text-xl md:text-2xl tracking-tight">4+1</span>
              <span className="block text-gray-500 text-[10px] md:text-xs font-semibold tracking-[0.1em] md:tracking-[0.2em] uppercase mt-1 md:mt-2">Geniş Odalar</span>
            </div>
            <div className="p-1 md:p-2 group">
              <span className="text-brand-color-primary text-3xl md:text-4xl mb-2 md:mb-4 flex justify-center transform group-hover:scale-110 transition-transform duration-300"><FiMaximize2 /></span>
              <span className="block text-gray-900 font-bold text-xl md:text-2xl tracking-tight">Premium</span>
              <span className="block text-gray-500 text-[10px] md:text-xs font-semibold tracking-[0.1em] md:tracking-[0.2em] uppercase mt-1 md:mt-2">Yaşam Alanı</span>
            </div>
            <div className="p-1 md:p-2 group">
              <span className="text-brand-color-primary text-3xl md:text-4xl mb-2 md:mb-4 flex justify-center transform group-hover:scale-110 transition-transform duration-300"><FiDroplet /></span>
              <span className="block text-gray-900 font-bold text-xl md:text-2xl tracking-tight">Müstakil</span>
              <span className="block text-gray-500 text-[10px] md:text-xs font-semibold tracking-[0.1em] md:tracking-[0.2em] uppercase mt-1 md:mt-2">Yüzme Havuzu</span>
            </div>
            <div className="p-1 md:p-2 group border-l-0 md:border-l">
              <span className="text-brand-color-primary text-3xl md:text-4xl mb-2 md:mb-4 flex justify-center transform group-hover:scale-110 transition-transform duration-300"><FiSun /></span>
              <span className="block text-gray-900 font-bold text-xl md:text-2xl tracking-tight">Fethiye</span>
              <span className="block text-gray-500 text-[10px] md:text-xs font-semibold tracking-[0.1em] md:tracking-[0.2em] uppercase mt-1 md:mt-2">Eşsiz Konum</span>
            </div>
          </div>
        </section>

        {/* ABOUT PROJECT */}
        <section className="py-24 px-6 max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-stretch gap-16">
            <div className="w-full md:w-1/2 flex flex-col justify-center">
              <div className="flex items-center gap-4 mb-6">
                 <div className="w-12 h-[2px] bg-brand-color-primary"></div>
                 <h2 className="text-xs font-bold text-brand-color-primary uppercase tracking-[0.3em]">Projeye Dair</h2>
              </div>
              <h3 className="text-4xl md:text-5xl font-light text-gray-900 mb-8 leading-tight tracking-tight">
                Doğa ile <br/><span className="font-bold text-brand-color-primary tracking-normal block md:inline mt-2 md:mt-0">Ortak Notanız !</span>
              </h3>
              <p className="text-gray-600 text-lg leading-relaxed mb-6 font-light">
                Her anında doğanın ve konforun tadını çıkarabileceğiniz bu villalar, özellikle Babadağ&apos;ın muhteşem manzarasıyla öne çıkıyor. Sabah kahvenizi içerken günün ilk ışıkları Babadağ&apos;ın zirvesinden yansırken, akşam güneşi muazzam manzarayla vedalaşıyor. Bu eşsiz atmosferde özel bir hikaye yazma fırsatını sunuyor.
              </p>
              <p className="text-gray-500 leading-relaxed mb-8">
                Günlük hayatın stres ve rutininden kış aylarında da kaçabilmeniz için hamam, sauna, kapalı havuz ve bar alanıyla tatil konforunuzu yuvanızda yaşayın. Geniş ve ferah salonu ile işlevselliğin ön plana alındığı mutfağı konforu yeniden tanımlıyor. Bölgenin eşsiz doğasına nazır, her köşesi lüks ve konfor detaylarıyla dikkat çeken yemyeşil bir bahçe alanı.
              </p>
              
              <div className="mt-auto pt-6 w-full max-w-full overflow-hidden">
                <Fancybox>
                  
                  {/* MOBILE: Thumbnail Slider */}
                  <div className="block md:hidden w-full">
                    {/* Main Image Slider */}
                    <Swiper
                      modules={[Navigation, Autoplay]}
                      spaceBetween={10}
                      navigation={true}
                      onSwiper={(swiper) => { mainSwiperRef.current = swiper; }}
                      onSlideChange={handleSlideChange}
                      autoplay={{ delay: 3500, disableOnInteraction: false }}
                      className="w-full aspect-[4/3] rounded-xl overflow-hidden mb-3 shadow-md"
                    >
                      {galleryImages.slice(0, 10).map((src, idx) => (
                        <SwiperSlide key={idx}>
                           <a href={src} data-fancybox="mobile-gallery" className="w-full h-full block cursor-zoom-in relative">
                             <img src={src} className="w-full h-full object-cover" />
                             <div className="absolute top-3 right-3 bg-black/50 text-white rounded-full p-2 backdrop-blur-sm">
                               <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"></path></svg>
                             </div>
                           </a>
                        </SwiperSlide>
                      ))}
                    </Swiper>
                    
                    {/* Thumbnails Slider (Manual State) */}
                    <Swiper
                      onSwiper={(swiper) => { thumbsSwiperRef.current = swiper; }}
                      spaceBetween={10}
                      slidesPerView={4.5}
                      className="w-full h-16"
                    >
                      {galleryImages.slice(0, 10).map((src, idx) => (
                        <SwiperSlide key={idx} onClick={() => mainSwiperRef.current && mainSwiperRef.current.slideTo(idx)} className={`cursor-pointer transition-all border-2 rounded-lg overflow-hidden shrink-0 ${idx === 0 ? 'opacity-100 border-brand-color-primary' : 'opacity-50 border-transparent'}`}>
                          <img src={src} className="w-full h-full object-cover" />
                        </SwiperSlide>
                      ))}
                    </Swiper>
                    
                    <a href={galleryImages[10]} data-fancybox="mobile-gallery" className="mt-4 w-full bg-slate-50 text-brand-color-primary font-bold py-3 px-4 rounded-xl flex items-center justify-between border border-gray-200 shadow-sm">
                       <span>Tüm Fotoğrafları Gör</span>
                       <span className="bg-brand-color-primary/10 px-3 py-1 rounded-lg text-sm">+{galleryImages.length - 10} Fotoğraf</span>
                    </a>
                    <div className="hidden">
                      {galleryImages.slice(11).map((src, idx) => (
                        <a key={idx + 11} href={src} data-fancybox="mobile-gallery"></a>
                      ))}
                    </div>
                  </div>

                  {/* DESKTOP: Mini Gallery row with fixed 'Tümünü Gör' */}
                  <div className="hidden md:flex gap-3 items-stretch">
                    <div className="flex-1 w-0">
                      <Swiper
                        modules={[Autoplay, Navigation]}
                        spaceBetween={12}
                        slidesPerView={2.5}
                        breakpoints={{
                          768: { slidesPerView: 2.5 },
                          1024: { slidesPerView: 3.5 },
                          1280: { slidesPerView: 4.5 },
                        }}
                        navigation={false}
                        autoplay={{ delay: 3500, disableOnInteraction: false }}
                        className="w-full !pb-2"
                      >
                        {galleryImages.slice(0, 10).map((src, idx) => (
                          <SwiperSlide key={idx}>
                            <a href={src} data-fancybox="mini-gallery" className="w-full aspect-square rounded-xl overflow-hidden shadow-md group relative block cursor-zoom-in border border-gray-100 hover:border-brand-color-primary transition-colors">
                              <img src={src} alt={`Lujo Emo Villas - ${idx + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500"></div>
                            </a>
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    </div>
                    
                    <div className="w-[28%] lg:w-[22%] xl:w-[18%] shrink-0 pb-2">
                      <a href={galleryImages[10]} data-fancybox="mini-gallery" className="w-full h-full rounded-xl overflow-hidden shadow-md relative flex flex-col group cursor-pointer bg-slate-50 items-center justify-center border border-gray-200 hover:border-brand-color-primary transition-colors duration-300">
                        <img src={galleryImages[10]} alt="Tüm fotoğraflar" className="absolute inset-0 w-full h-full object-cover opacity-20 group-hover:opacity-10 transition-opacity duration-300" />
                        <div className="relative z-10 flex flex-col items-center justify-center text-center px-1">
                          <span className="text-brand-color-primary font-bold text-lg md:text-xl mb-1">+{galleryImages.length - 10}</span>
                          <span className="text-gray-900 text-[9px] md:text-[10px] font-bold leading-tight uppercase">Tümünü Gör</span>
                        </div>
                      </a>
                    </div>
                  </div>

                  {/* Hidden links for desktop gallery */}
                  <div className="hidden md:block">
                    {galleryImages.slice(11).map((src, idx) => (
                      <a key={idx + 11} href={src} data-fancybox="mini-gallery" className="hidden"></a>
                    ))}
                  </div>
                </Fancybox>
              </div>
            </div>
            <div className="w-full md:w-1/2">
              {/* Promo Video with Fancybox */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl h-full min-h-[300px] md:min-h-full bg-gray-900 group">
                <Fancybox>
                  <a href="https://cdn.villavillam.com.tr/lujo-emo-villas/video.MP4" data-fancybox="promo-video" className="absolute inset-0 w-full h-full block cursor-pointer">
                    <img 
                      src={videoPoster} 
                      alt="Lujo Emo Villas Tanıtım" 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors duration-500"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-20 h-20 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-[0_10px_40px_rgba(0,0,0,0.3)] group-hover:scale-110 transition-transform duration-300">
                        <svg className="w-8 h-8 text-brand-color-primary ml-1" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M4 4l12 6-12 6z"></path>
                        </svg>
                      </div>
                    </div>
                  </a>
                </Fancybox>
              </div>
            </div>
          </div>
        </section>

        {/* LISTING DETAILS */}
        <section className="pb-24 pt-12 px-6 max-w-5xl mx-auto">
          <div className="flex items-center gap-4 mb-10 justify-center">
             <div className="w-12 h-[1px] bg-brand-color-primary"></div>
             <h2 className="text-xs font-bold text-brand-color-primary uppercase tracking-[0.3em] text-center">İlan Detayları</h2>
             <div className="w-12 h-[1px] bg-brand-color-primary"></div>
          </div>
          
          <div className="bg-white rounded-[2rem] p-8 md:p-12 shadow-[0_10px_40px_rgba(0,0,0,0.04)] border border-gray-100">
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-2">
              {[
                { label: 'İlan No', value: 'LUJO-01' },
                { label: 'Emlak Tipi', value: 'Satılık Villa' },
                { label: 'Brüt m²', value: '450' },
                { label: 'Net m²', value: '372' },
                { label: 'Arsa Payı', value: '500 m²' },
                { label: 'Oda Sayısı', value: '4+1' },
                { label: 'Banyo Sayısı', value: '5' },
                { label: 'Bina Yaşı', value: '0 (Yeni)' },
                { label: 'Isıtma', value: 'Yerden Isıtma + VRF' },
                { label: 'Krediye Uygun mu?', value: 'Evet' },
                { label: 'Eşya Durumu', value: 'Opsiyonel' }
              ].map((item, idx) => (
                <li key={idx} className="flex justify-between items-center py-4 border-b border-gray-100/70 hover:bg-slate-50/50 transition-colors px-3 rounded-xl -mx-3">
                  <span className="text-gray-500 font-medium text-sm md:text-base">{item.label}</span>
                  <span className="text-gray-900 font-bold text-sm md:text-base">{item.value}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* GALLERY SWIPER - HIDDEN AS REQUESTED */}
        {/* <section id="galeri" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 mb-12 text-center">
             <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Proje Galerisi</h2>
             <p className="text-gray-500 max-w-2xl mx-auto">Lujo Emo Villas projemizin iç ve dış mekanlarını daha yakından inceleyin.</p>
          </div>
          
          <div className="w-full relative px-4 md:px-0 lg:px-6 max-w-7xl mx-auto">
            <Fancybox options={{ infinite: true }}>
              <Swiper
                modules={[Navigation, Pagination, Autoplay]}
                spaceBetween={20}
                slidesPerView={1}
                breakpoints={{
                  640: { slidesPerView: 2 },
                  1024: { slidesPerView: 3 },
                }}
                navigation
                pagination={{ clickable: true, dynamicBullets: true }}
                autoplay={{ delay: 3500, disableOnInteraction: false }}
                className="w-full pb-12"
              >
                {galleryImages.map((src, index) => (
                  <SwiperSlide key={index}>
                    <a 
                      href={src}
                      data-fancybox="gallery"
                      className="w-full aspect-[4/3] relative rounded-xl overflow-hidden shadow-lg group block cursor-zoom-in"
                    >
                      <img 
                        src={src} 
                        alt={`Lujo Emo Villas Fethiye - ${index + 1}`} 
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" 
                      />
                      <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors duration-500"></div>
                    </a>
                  </SwiperSlide>
                ))}
              </Swiper>
            </Fancybox>
          </div>
        </section> */}

        {/* FLOOR PLANS */}
        <section className="py-24 px-6 max-w-6xl mx-auto bg-slate-50">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Kat Planları</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">Maksimum verimlilik ve konfor için tasarlanmış geniş yaşam alanları. İlgili kat planını PDF olarak indirip inceleyebilirsiniz.</p>
          </div>

          <div className="flex overflow-x-auto snap-x snap-mandatory md:grid md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6 pb-6 -mx-6 px-6 md:mx-0 md:px-0 md:pb-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            {floorPlans.map((plan) => (
              <a 
                key={plan.id}
                href={plan.url}
                target="_blank"
                rel="noopener noreferrer"
                className="min-w-[150px] flex-shrink-0 snap-center group flex flex-col items-center justify-center p-6 bg-white rounded-3xl shadow-sm border border-gray-100 hover:shadow-xl hover:border-brand-color-primary/30 transition-all duration-300"
              >
                <div className="w-16 h-16 rounded-full bg-brand-color-primary/5 text-brand-color-primary flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <FiFileText size={24} />
                </div>
                <h4 className="text-gray-800 font-bold mb-2 text-center group-hover:text-brand-color-primary transition-colors">{plan.name}</h4>
                <span className="text-sm text-gray-500 font-medium">İncele & İndir</span>
              </a>
            ))}
          </div>
        </section>

        {/* FEATURES & AMENITIES */}
        <section className="relative py-32 overflow-hidden bg-slate-900">
          {/* Subtle Decorative Gradients */}
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
             <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-brand-color-primary/20 blur-[120px]"></div>
             <div className="absolute -bottom-[20%] -right-[10%] w-[40%] h-[60%] rounded-full bg-blue-500/10 blur-[100px]"></div>
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-6">
            <div className="mb-20 text-center">
              <span className="text-brand-color-primary text-sm font-bold tracking-[0.3em] uppercase mb-4 block">Ayrıcalıklı Yaşam</span>
              <h2 className="text-4xl md:text-5xl font-light text-white mb-6">Donanım ve <span className="font-bold">Özellikler</span></h2>
              <p className="text-gray-400 max-w-2xl mx-auto text-lg leading-relaxed">
                Her villada standart olarak sunulan premium özelliklerle, hayal ettiğinizden daha fazlasını deneyimleyin.
              </p>
            </div>
            
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-6">
              {features.map((feature, idx) => (
                <div 
                  key={idx} 
                  className="group relative flex flex-col items-center justify-start md:justify-center text-center p-3 md:p-8 rounded-2xl md:rounded-3xl bg-white/[0.03] backdrop-blur-xl border border-white/10 hover:bg-white/[0.08] hover:border-brand-color-primary/50 transition-all duration-500 hover:-translate-y-1 md:hover:-translate-y-2 cursor-default"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-brand-color-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl md:rounded-3xl"></div>
                  
                  <div className="relative w-10 h-10 md:w-16 md:h-16 mb-3 md:mb-6 rounded-xl md:rounded-2xl bg-white/5 flex items-center justify-center text-brand-color-primary group-hover:scale-110 group-hover:bg-brand-color-primary group-hover:text-white transition-all duration-500 shadow-md md:shadow-lg group-hover:shadow-brand-color-primary/30">
                    <FiCheckCircle className="w-5 h-5 md:w-7 md:h-7" />
                  </div>
                  <h4 className="relative text-white/90 font-medium text-[11px] md:text-lg leading-tight md:leading-snug group-hover:text-white transition-colors">
                    {feature}
                  </h4>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* LOCATION & DISTANCES */}
        <section className="py-24 px-6 max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-12 items-center">
            <div className="w-full lg:w-2/5">
              <span className="text-brand-color-primary text-sm font-bold tracking-[0.3em] uppercase mb-4 block">Konum</span>
              <h2 className="text-3xl md:text-5xl font-light text-gray-900 mb-6">Mükemmel <span className="font-bold">Lokasyon</span></h2>
              <p className="text-gray-600 mb-10 text-lg leading-relaxed">
                Doğanın kalbinde, ancak şehrin olanaklarına bir o kadar yakın. İhtiyaç duyduğunuz her yere kolayca ulaşın.
              </p>
              
              <ul className="space-y-3 md:space-y-4 p-0 m-0 w-full">
                {[
                  { id: 'fethiye', name: 'Fethiye Merkez', time: '10 Dakika' },
                  { id: 'oludeniz', name: 'Ölüdeniz', time: '5 Dakika' },
                  { id: 'kayakoy', name: 'Kayaköy', time: '10 Dakika' },
                  { id: 'dalaman', name: 'Dalaman Havalimanı', time: '60 Dakika' }
                ].map((loc) => (
                  <li 
                    key={loc.id}
                    onMouseEnter={() => setHoveredLoc(loc.id)}
                    onMouseLeave={() => setHoveredLoc(null)}
                    className={`flex items-center justify-between p-3 md:p-5 rounded-xl md:rounded-2xl border transition-all duration-300 cursor-pointer w-full ${hoveredLoc === loc.id ? 'bg-white shadow-xl border-brand-color-primary/30 scale-[1.02]' : 'border-transparent hover:bg-white hover:shadow-md'}`}
                  >
                    <div className="flex items-center gap-3 md:gap-4 text-gray-800 font-medium text-sm md:text-lg">
                      <span className={`w-8 h-8 md:w-10 md:h-10 shrink-0 rounded-full flex items-center justify-center transition-all duration-300 ${hoveredLoc === loc.id ? 'bg-brand-color-primary text-white scale-110' : 'bg-gray-100 text-gray-500'}`}>
                        <FiMapPin className="w-4 h-4 md:w-5 md:h-5" />
                      </span>
                      <span className="truncate">{loc.name}</span>
                    </div>
                    <span className={`font-bold text-xs md:text-base whitespace-nowrap transition-colors duration-300 ${hoveredLoc === loc.id ? 'text-brand-color-primary' : 'text-gray-500'}`}>
                      {loc.time}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="w-full lg:w-3/5">
              {/* Interactive Designed Custom Map Area */}
              <div className="w-full h-[400px] md:h-[600px] bg-slate-100 rounded-[3rem] overflow-hidden relative shadow-2xl border-4 border-white group">
                
                {/* Styled Map iframe */}
                <iframe 
                  ref={mapIframeRef}
                  width="100%" 
                  height="100%" 
                  frameBorder="0" 
                  scrolling="no" 
                  marginHeight="0" 
                  marginWidth="0" 
                  src="/map.html" 
                  className="absolute inset-0 w-full h-full"
                ></iframe>
                
                {/* Overlay gradient to soften the edges */}
                <div className="absolute inset-0 bg-gradient-to-tr from-brand-color-primary/5 via-transparent to-black/5 pointer-events-none"></div>

              </div>
            </div>
          </div>
        </section>

        {/* CONTACT / CTA */}
        <section id="iletisim" className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 z-0">
             <img src={ctaImage} alt="Background" className="w-full h-full object-cover blur-sm scale-110" />
             <div className="absolute inset-0 bg-gray-900/80 backdrop-blur-md"></div>
          </div>
          
          <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">Yerinizi Almak İçin Geç Kalmayın</h2>
            <p className="text-gray-300 text-lg mb-12 max-w-2xl mx-auto">
              Sadece 5 adet üretilen Lujo Emo Villas projesi hakkında detaylı bilgi, fiyat teklifi ve yerinde sunum için bize ulaşın.
            </p>
            
            <div className="bg-white rounded-[2rem] p-8 md:p-12 shadow-2xl max-w-2xl mx-auto">
              <div className="flex flex-col gap-6">
                <a 
                  href="tel:+902526060505" 
                  className="flex items-center justify-center gap-4 w-full bg-brand-color-primary text-white font-bold py-5 px-6 rounded-2xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 text-lg md:text-xl"
                >
                  <FiPhone size={28} />
                  +90 252 606 05 05
                </a>
                
                <a 
                  href="https://wa.me/908503467049?text=Lujo%20Emo%20Villas%20projesi%20hakk%C4%B1nda%20bilgi%20almak%20istiyorum." 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-4 w-full bg-[#25D366] text-white font-bold py-5 px-6 rounded-2xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 text-lg md:text-xl"
                >
                  <FaWhatsapp size={32} />
                  WhatsApp&apos;tan İletişime Geç
                </a>
                
                <a 
                  href="mailto:info@villavillam.com.tr" 
                  className="flex items-center justify-center gap-3 w-full bg-slate-50 text-gray-700 font-bold py-5 px-6 rounded-2xl hover:bg-slate-100 transition-colors duration-300 text-base md:text-lg mt-2 border border-slate-100"
                >
                  <FiMail size={24} className="text-gray-400" />
                  info@villavillam.com.tr
                </a>
              </div>
            </div>
          </div>
        </section>
        
        <style dangerouslySetInnerHTML={{__html: `
          @keyframes scaleUp {
            0% { transform: scale(1); }
            100% { transform: scale(1.1); }
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fadeIn {
            animation: fadeIn 0.5s ease-out forwards;
          }
        `}} />
      </main>
    </Layout>
  );
}

export async function getStaticProps() {
  const headerContent = await fetcher(
    process.env.NEXT_PUBLIC_API + "/FullScreenMenu"
  );

  return {
    props: {
      data: {
        result: {
          title: "Lujo Emo Villas Fethiye - Lansmana Özel Satılık Villalar",
          description: "Lujo Emo Villas Fethiye projemizde yer alan sınırlı sayıdaki premium satılık villalarımızı inceleyin.",
          Structural: [],
          static: {
            GetOffer: null
          },
          footer: "0"
        },
        RoutingTypeId: 0,
        headerContent: headerContent || null,
      }
    },
    revalidate: 60
  }
}
